import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';

import { SharedService } from 'src/app/core/services/shared.service';
import { BackendResponse } from 'src/app/core/interfaces/shared.interface';
import { Investment } from '../investments/investments.interface';
import {
  InvestorPayload,
  NorwegianCompanyPayload,
  PayloadAddress
} from './pages/pitch/components/invest-on-behalf-modal/invest-on-behalf.interface';
import { InvestmentCompany } from '../investment-companies/investment-companies.interface';
import * as pitchesInterface from './pitches.interface';
import { HistoryRecord, UserProfile } from './pitches.interface';
import { UserInDetail } from '../users/users.interface';
import { DeckHttpClient } from 'schema';
import { Amplify, Geo } from 'aws-amplify';
import { iso31661 } from 'iso-3166';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';


interface ExportInvestorsToAdminflow {
  allocation_date: string;
  share_class: string;
  investments_ids?: string[];
}

type PitchId = pitchesInterface.Pitch['id'];
const identityPoolId = environment.AWS_COGNITO_POOL;
const INDEX_ID = environment.AWS_LOCATION_INDEX;
const region = identityPoolId.split(':')[0];
Amplify.configure({
  Auth: { identityPoolId, region },
  geo: { amazon_location_service: { region, search_indices: { items: [INDEX_ID], default: INDEX_ID } } }
});
const OSLO_COORDINATES = [59.908587599025026, 10.74116670406494] as [number, number];

@Injectable({
  providedIn: 'root'
})
export class PitchesService {
  constructor(private http: DeckHttpClient, private sharedService: SharedService) {}

  acceptPendingInvestments(id: PitchId) {
    return this.http.patch<null>(`${this.sharedService.baseUrl}pitch/${id}/accept-investments`, null);
  }

  addNewInvestmentCompany(data: NorwegianCompanyPayload | InvestorPayload): Observable<InvestmentCompany> {
    return this.http.post<InvestmentCompany>(`${this.sharedService.baseUrl}investment-companies`, data);
  }

  addUser(data: { first_name: string; last_name: string; email: string; pnr: string }): Observable<pitchesInterface.PotentialInvestor> {
    return this.http.usersCreate({ DeckCreateUserRequest: data });
  }

  askForCompanies(params: { size: number; navn: string }): Observable<any> {
    return this.http.get<any>('https://data.brreg.no/enhetsregisteret/api/enheter/', { params: this.sharedService.createParams(params) });
  }

  changeInvestmentsPaidStatus(data: { ids: Investment['id'][]; paid?: string }) {
    return this.http.patch<null>(`${this.sharedService.baseUrl}investment/batch-paid`, data);
  }

  changeInvestmentsStatus(data: { ids: Investment['id'][]; status?: string }) {
    return this.http.patch<null>(`${this.sharedService.baseUrl}investment/batch-status`, data);
  }

  changeMultiStatus(data: { ids: PitchId[]; visibility?: string }) {
    return this.http.patch<pitchesInterface.Pitch[]>(`${this.sharedService.baseUrl}pitch/batch-status`, data);
  }

  changePitch(id: PitchId, data: { payment_due?: string; end_day?: Date; account_number?: string }): Observable<pitchesInterface.Pitch> {
    return this.http.patch<pitchesInterface.Pitch>(`${this.sharedService.baseUrl}pitch/${id}`, data);
  }

  changeSharesNumber(data: { ids: Investment['id'][]; shares?: number }) {
    return this.http.patch<null>(`${this.sharedService.baseUrl}investment/batch-shares`, data);
  }

  exportInvestors(id: PitchId): Observable<string> {
    return this.http.get<string>(`${this.sharedService.baseUrl}pitch/${id}/download-investments`, {
      responseType: 'text' as 'json'
    });
  }

  exportToAdminflow(id: PitchId, data: ExportInvestorsToAdminflow) {
    return this.http.post<pitchesInterface.ExportedInvestment>(`${this.sharedService.baseUrl}pitch/${id}/export-investments`, data);
  }

  getInvitedInvestors(id: PitchId) {
    return this.http.get<BackendResponse<pitchesInterface.InvitedInvestor>>(`${this.sharedService.baseUrl}pitch/${id}/invitations`);
  }

  getPitch(id: PitchId): Observable<pitchesInterface.Pitch> {
    return this.http.get<pitchesInterface.Pitch>(`${this.sharedService.baseUrl}pitch/${id}`);
  }

  getPitchChangesHistory(id: PitchId): Observable<HistoryRecord[]> {
    return this.http.get<HistoryRecord[]>(`${this.sharedService.baseUrl}pitch/${id}/history`);
  }

  getPitches(params?: pitchesInterface.PitchesParams) {
    return this.http.get<BackendResponse<pitchesInterface.Pitch>>(`${this.sharedService.baseUrl}pitch`, {
      params: this.sharedService.createParams(params)
    });
  }

  getProfile(id: UserProfile['id']) {
    return this.http.usersProfileRetrieve({ id });
  }

  investOnBehalf(data: pitchesInterface.InvestmentData) {
    return this.http.post<Investment>(`${this.sharedService.baseUrl}investment/invest-on-behalf`, data);
  }

  searchForPlace(string) {
    return from(Geo.searchForSuggestions(string, { biasPosition: OSLO_COORDINATES }));
  }

  getPlace(placeId) {
    return from(Geo.searchByPlaceId(placeId)).pipe(map((place) => ({
      ...place,
      countryFullName: place.country === 'NOR' ? 'Norge' : iso31661.find((c) => c.alpha3 === place.country).name,
      firstLine: [place.street, place.addressNumber].filter(Boolean).join(' ')
    })));
  }

  searchUsers(params?: pitchesInterface.PotentialInvestorParams) {
    return this.http.usersSearchList(params);
  }

  sendInvitation(id: PitchId, email: string): Observable<pitchesInterface.InvitedInvestor> {
    return this.http.post<pitchesInterface.InvitedInvestor>(`${this.sharedService.baseUrl}pitch/${id}/invite`, { email });
  }

  sendPaymentSlip(data: { investment_id: Investment['id'] }) {
    return this.http.post<{ id: string }>(`${this.sharedService.baseUrl}investment/${data.investment_id}/send-payment-slip`, {});
  }

  switchFeatured(id: PitchId): Observable<pitchesInterface.Pitch> {
    return this.http.patch<pitchesInterface.Pitch>(`${this.sharedService.baseUrl}pitch/${id}/featured`, null);
  }

  updateProfile(id: UserInDetail['id'], data: { profile: { pnr?: string; address: PayloadAddress } }) {
    return this.http.usersPartialUpdate({ id, PatchedDeckUserProfileRequest: data });
  }
}
